import React, { useState, useEffect, useCallback, Suspense, lazy } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, MapPin, Calendar, Clock, Users, Car, Phone, Mail,
  MessageSquare, Star, Loader2, AlertCircle, CheckCircle, XCircle,
  Navigation, CreditCard, FileText, ChevronRight
} from 'lucide-react';
import { Navbar } from '../layout/Navbar';
import { Footer } from '../layout/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { taxiBooking } from '@/lib/api';
import TaxiChatWindow, { FloatingTaxiChat } from './TaxiChatWindow';

// Lazy load the map
const LiveTrackingMap = lazy(() => import('./LiveTrackingMap'));

const TaxiBookingDetail = () => {
  const { t, i18n } = useTranslation();
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const currentLang = i18n.language?.split('-')[0] || 'tr';
  
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState(null);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showRatingDialog, setShowRatingDialog] = useState(false);
  const [showChatModal, setShowChatModal] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [rating, setRating] = useState(5);
  const [ratingComment, setRatingComment] = useState('');
  const [submittingRating, setSubmittingRating] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  const fetchBooking = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await taxiBooking.getBooking(bookingId);
      setBooking(response.data);
    } catch (err) {
      console.error('Error fetching booking:', err);
      setError(err.response?.data?.detail || t('common.error'));
    } finally {
      setLoading(false);
    }
  }, [bookingId, t]);

  useEffect(() => {
    // Get current user from localStorage
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        setCurrentUser(JSON.parse(userStr));
      } catch (e) {
        console.warn('Failed to parse user from localStorage');
      }
    }
    
    fetchBooking();
  }, [fetchBooking]);

  const handleCancel = async () => {
    try {
      setCancelling(true);
      await taxiBooking.cancelBooking(bookingId);
      toast.success(t('taxi.bookingCancelled', 'Booking cancelled'));
      setShowCancelDialog(false);
      fetchBooking();
    } catch (err) {
      console.error('Error cancelling booking:', err);
      toast.error(err.response?.data?.detail || t('common.error'));
    } finally {
      setCancelling(false);
    }
  };

  const handleRating = async () => {
    try {
      setSubmittingRating(true);
      await taxiBooking.rateBooking(bookingId, { 
        rating, 
        comment: ratingComment 
      });
      toast.success(t('taxi.ratingSubmitted', 'Rating submitted'));
      setShowRatingDialog(false);
      fetchBooking();
    } catch (err) {
      console.error('Error submitting rating:', err);
      toast.error(err.response?.data?.detail || t('common.error'));
    } finally {
      setSubmittingRating(false);
    }
  };

  const getStatusConfig = (status) => {
    const configs = {
      pending: { 
        label: t('taxi.statusPending', 'Pending'), 
        className: 'bg-yellow-100 text-yellow-800 border-yellow-200',
        icon: Clock
      },
      confirmed: { 
        label: t('taxi.statusConfirmed', 'Confirmed'), 
        className: 'bg-green-100 text-green-800 border-green-200',
        icon: CheckCircle
      },
      in_progress: { 
        label: t('taxi.statusInProgress', 'In Progress'), 
        className: 'bg-blue-100 text-blue-800 border-blue-200',
        icon: Navigation
      },
      completed: { 
        label: t('taxi.statusCompleted', 'Completed'), 
        className: 'bg-gray-100 text-gray-800 border-gray-200',
        icon: CheckCircle
      },
      cancelled: { 
        label: t('taxi.statusCancelled', 'Cancelled'), 
        className: 'bg-red-100 text-red-800 border-red-200',
        icon: XCircle
      },
    };
    return configs[status] || configs.pending;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="container mx-auto px-4 py-12">
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !booking) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="container mx-auto px-4 py-12">
          <Card className="max-w-lg mx-auto">
            <CardContent className="p-8 text-center">
              <AlertCircle className="w-16 h-16 mx-auto text-red-500 mb-4" />
              <h2 className="text-xl font-bold mb-2">{t('taxi.bookingNotFound', 'Booking Not Found')}</h2>
              <p className="text-muted-foreground mb-4">{error}</p>
              <Button onClick={() => navigate(`/${currentLang}/taxi-limousine`)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t('common.back', 'Back')}
              </Button>
            </CardContent>
          </Card>
        </div>
        <Footer />
      </div>
    );
  }

  const statusConfig = getStatusConfig(booking.status);
  const StatusIcon = statusConfig.icon;
  const showTracking = ['confirmed', 'in_progress'].includes(booking.status);
  const canCancel = ['pending', 'confirmed'].includes(booking.status);
  const canRate = booking.status === 'completed' && !booking.rated;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="taxi-booking-detail">
      <Navbar />
      
      <div className="container mx-auto px-4 py-8">
        {/* Back button */}
        <Button 
          variant="ghost" 
          onClick={() => navigate(`/${currentLang}/taxi-limousine`)}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('common.back', 'Back')}
        </Button>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Booking Header */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Car className="w-6 h-6 text-orange-500" />
                      {t('taxi.booking', 'Booking')} #{booking.booking_number}
                    </CardTitle>
                    <CardDescription>
                      {t('taxi.bookingType', 'Type')}: {booking.booking_type === 'transfer' ? t('taxi.transfer', 'Transfer') : 
                       booking.booking_type === 'hourly' ? t('taxi.hourly', 'Hourly') : t('taxi.delivery', 'Delivery')}
                    </CardDescription>
                  </div>
                  <Badge variant="outline" className={statusConfig.className}>
                    <StatusIcon className="w-4 h-4 mr-1" />
                    {statusConfig.label}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Route */}
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-4 h-4 text-green-600" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">{t('taxi.pickup', 'Pickup')}</p>
                      <p className="font-medium">{booking.pickup_address}</p>
                    </div>
                  </div>
                  
                  {booking.dropoff_address && (
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-4 h-4 text-red-600" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">{t('taxi.dropoff', 'Drop-off')}</p>
                        <p className="font-medium">{booking.dropoff_address}</p>
                      </div>
                    </div>
                  )}
                </div>

                <Separator />

                {/* Date & Time */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <p className="text-xs text-muted-foreground">{t('taxi.date', 'Date')}</p>
                      <p className="font-medium">
                        {new Date(booking.pickup_datetime).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <p className="text-xs text-muted-foreground">{t('taxi.time', 'Time')}</p>
                      <p className="font-medium">
                        {new Date(booking.pickup_datetime).toLocaleTimeString([], { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Passengers */}
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-muted-foreground" />
                    <span>{booking.passengers} {t('taxi.passengers', 'passengers')}</span>
                  </div>
                  {booking.luggage > 0 && (
                    <div className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-muted-foreground" />
                      <span>{booking.luggage} {t('taxi.luggage', 'luggage')}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Live Tracking Map */}
            {showTracking && (
              <Suspense fallback={
                <Card>
                  <CardContent className="p-8 flex items-center justify-center">
                    <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
                  </CardContent>
                </Card>
              }>
                <LiveTrackingMap 
                  bookingId={bookingId}
                  onOpenChat={() => setShowChatModal(true)}
                  height="350px"
                />
              </Suspense>
            )}

            {/* Vehicle & Driver Info */}
            {booking.vehicle && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">{t('taxi.vehicleInfo', 'Vehicle Information')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-orange-100 rounded-lg flex items-center justify-center">
                      <Car className="w-8 h-8 text-orange-600" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold">{booking.vehicle.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {booking.vehicle.brand} {booking.vehicle.model}
                      </p>
                      <div className="flex items-center gap-3 mt-1">
                        <Badge variant="outline" className="capitalize">
                          {booking.vehicle.vehicle_class}
                        </Badge>
                        <span className="text-sm text-muted-foreground">
                          {booking.vehicle.capacity_passengers} {t('taxi.seats', 'seats')}
                        </span>
                      </div>
                    </div>
                  </div>

                  {booking.driver_name && (
                    <>
                      <Separator className="my-4" />
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                            <Users className="w-5 h-5 text-gray-600" />
                          </div>
                          <div>
                            <p className="font-medium">{booking.driver_name}</p>
                            <p className="text-sm text-muted-foreground">{t('taxi.driver', 'Driver')}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {booking.driver_phone && (
                            <Button variant="outline" size="sm" asChild>
                              <a href={`tel:${booking.driver_phone}`}>
                                <Phone className="w-4 h-4 mr-1" />
                                {t('taxi.call', 'Call')}
                              </a>
                            </Button>
                          )}
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => setShowChatModal(true)}
                          >
                            <MessageSquare className="w-4 h-4 mr-1" />
                            {t('taxi.chat', 'Chat')}
                          </Button>
                        </div>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Price Summary */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{t('taxi.priceSummary', 'Price Summary')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-muted-foreground">{t('taxi.tripPrice', 'Trip Price')}</span>
                  <span className="font-semibold text-lg">
                    {booking.currency === 'EUR' ? '€' : booking.currency === 'TRY' ? '₺' : '$'}
                    {booking.price}
                  </span>
                </div>
                
                <Separator />
                
                <div className="flex justify-between items-center">
                  <span className="font-semibold">{t('taxi.total', 'Total')}</span>
                  <span className="font-bold text-xl text-orange-600">
                    {booking.currency === 'EUR' ? '€' : booking.currency === 'TRY' ? '₺' : '$'}
                    {booking.price}
                  </span>
                </div>

                {booking.payment_status && (
                  <Badge 
                    variant="outline" 
                    className={
                      booking.payment_status === 'paid' 
                        ? 'bg-green-50 text-green-700 w-full justify-center' 
                        : 'bg-yellow-50 text-yellow-700 w-full justify-center'
                    }
                  >
                    <CreditCard className="w-4 h-4 mr-1" />
                    {booking.payment_status === 'paid' 
                      ? t('taxi.paid', 'Paid') 
                      : t('taxi.paymentPending', 'Payment Pending')}
                  </Badge>
                )}
              </CardContent>
            </Card>

            {/* Actions */}
            <Card>
              <CardContent className="p-4 space-y-3">
                {showTracking && (
                  <Button 
                    className="w-full bg-orange-500 hover:bg-orange-600"
                    onClick={() => setShowChatModal(true)}
                  >
                    <MessageSquare className="w-4 h-4 mr-2" />
                    {t('taxi.contactDriver', 'Contact Driver')}
                  </Button>
                )}
                
                {canRate && (
                  <Button 
                    className="w-full"
                    variant="outline"
                    onClick={() => setShowRatingDialog(true)}
                  >
                    <Star className="w-4 h-4 mr-2" />
                    {t('taxi.rateTrip', 'Rate Trip')}
                  </Button>
                )}
                
                {canCancel && (
                  <Button 
                    variant="outline"
                    className="w-full text-red-600 hover:bg-red-50 hover:text-red-700"
                    onClick={() => setShowCancelDialog(true)}
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    {t('taxi.cancelBooking', 'Cancel Booking')}
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Contact Info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{t('taxi.contactInfo', 'Contact Information')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-muted-foreground" />
                  <span>{booking.contact_name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-muted-foreground" />
                  <span>{booking.contact_phone}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-muted-foreground" />
                  <span className="text-sm">{booking.contact_email}</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Cancel Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('taxi.cancelBooking', 'Cancel Booking')}</DialogTitle>
          </DialogHeader>
          <p className="text-muted-foreground">
            {t('taxi.cancelConfirmation', 'Are you sure you want to cancel this booking? This action cannot be undone.')}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCancelDialog(false)}>
              {t('common.no', 'No')}
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleCancel}
              disabled={cancelling}
            >
              {cancelling && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {t('common.yes', 'Yes, Cancel')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rating Dialog */}
      <Dialog open={showRatingDialog} onOpenChange={setShowRatingDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('taxi.rateYourTrip', 'Rate Your Trip')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>{t('taxi.rating', 'Rating')}</Label>
              <div className="flex gap-2 mt-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setRating(star)}
                    className="focus:outline-none"
                  >
                    <Star 
                      className={`w-8 h-8 ${
                        star <= rating 
                          ? 'text-yellow-400 fill-yellow-400' 
                          : 'text-gray-300'
                      }`}
                    />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label>{t('taxi.comment', 'Comment')} ({t('common.optional', 'optional')})</Label>
              <Textarea
                value={ratingComment}
                onChange={(e) => setRatingComment(e.target.value)}
                placeholder={t('taxi.ratingPlaceholder', 'Tell us about your experience...')}
                rows={3}
                className="mt-2"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRatingDialog(false)}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button 
              onClick={handleRating}
              disabled={submittingRating}
              className="bg-orange-500 hover:bg-orange-600"
            >
              {submittingRating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {t('taxi.submitRating', 'Submit Rating')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Chat Modal */}
      {showChatModal && currentUser && booking.carrier_id && (
        <TaxiChatWindow
          bookingId={bookingId}
          participantId={booking.carrier_id}
          participantName={booking.driver_name || booking.carrier_name}
          bookingNumber={booking.booking_number}
          currentUserId={currentUser.id}
          isModal={true}
          isOpen={showChatModal}
          onClose={() => setShowChatModal(false)}
        />
      )}

      {/* Floating Chat for active trips */}
      {showTracking && currentUser && booking.carrier_id && !showChatModal && (
        <FloatingTaxiChat
          bookingId={bookingId}
          participantId={booking.carrier_id}
          participantName={booking.driver_name || booking.carrier_name}
          bookingNumber={booking.booking_number}
          currentUserId={currentUser.id}
        />
      )}

      <Footer />
    </div>
  );
};

export default TaxiBookingDetail;
